// PhotoTiles (job page round 3's PhotoRoll; BL-189 consolidation, with JobViews' PhotoStrip): a record's
// photos as tiles, each a button that opens the viewer.
// - line (Amir, 26 Oct: "do it the way Instagram does, tile them, with a timestamp under each one; full
//   width, scrolling left and right"): one line of square tiles, newest first, as wide as its section,
//   scrolling sideways and snapping to a tile, the time each was taken under it. ← and → move between
//   the tiles (Home and End to the ends) and bring the one focused into view. No counts and no labels on
//   a tile: what a photo is, where it belongs and who sees it are the viewer's. The job record's Photos
//   and forms.
// - grid: tiles a column wide (at least 140), 4:3, each with its name under it (a muted word first: its
//   kind), its lines (who took it; who sees it) and its actions at the end of the last line, as a
//   RowList row's (Links, 44 tall to the touch on a phone). A lede over them; the first few, then
//   "Show all N". The job's photos and files.
// A video's tile opens like a photo's; a file's opens in a new tab (PhotoThumb's faces). One on its way
// up is a dashed tile with its words; one whose file won't load says so on the tile. None: one muted
// line (line), or the sentence between dashed rules (grid). What a tile says is the caller's (rows/).
// Reads only --tiles-*; PhotoThumb and Link read their own.
import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from '../Link/Link.tsx';
import { PhotoThumb, type Photo } from '../PhotoThumb/PhotoThumb.tsx';
import type { RowAction } from '../RowList/RowList.tsx';
import './PhotoTiles.css';

/** One tile: its photo, and in the grid what's under it. */
export interface Tile {
  photo: Photo;
  /** A muted word before its name, what kind it is ("Before:"); alone, a name made up for it ("Equipment 1"). */
  kicker?: string;
  /** Its name under the tile, 13 / 600 ("The old tank, rust at the base"). */
  name?: ReactNode;
  /** Lines under its name (who took it; who sees it, a Label). Omitted, the time it was taken (photo.taken). */
  lines?: readonly ReactNode[];
  /** Its actions, at the end of its last line: Links, as a RowList row's. */
  actions?: readonly RowAction[];
  /** The tile's name for screen readers, in place of the photo's note ("Open … full size"). */
  openLabel?: string;
}

export interface PhotoTilesProps {
  tiles: readonly Tile[];
  /** Its name for screen readers ("The job’s photos, newest first"). */
  label: string;
  /** line: one sideways line of square tiles (the default); grid: tiles a column wide, 4:3. */
  layout?: 'line' | 'grid';
  /** all: each tile's hairline frame (the default); below: a rule under each tile only. */
  frame?: 'all' | 'below';
  /** A line over the tiles ("2 of 6 the customer sees."). */
  lede?: ReactNode;
  /** Shows this many, then "Show all N". Omitted, all of them. */
  first?: number;
  /** A tile was pressed: open the viewer at that photo. */
  onOpen?: (photoId: string) => void;
  /** What it says with none; null draws nothing (the caller says it). */
  emptyText?: string | null;
}

const KEYS = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];

function onLineKeyDown(e: KeyboardEvent<HTMLUListElement>) {
  if (!KEYS.includes(e.key)) return;
  const tiles = [...e.currentTarget.querySelectorAll<HTMLElement>('button.fl-thumb')];
  const i = tiles.indexOf(document.activeElement as HTMLElement);
  if (i < 0) return;
  const j =
    e.key === 'ArrowRight'
      ? Math.min(i + 1, tiles.length - 1)
      : e.key === 'ArrowLeft'
        ? Math.max(i - 1, 0)
        : e.key === 'Home'
          ? 0
          : tiles.length - 1;
  e.preventDefault();
  tiles[j]?.focus({ preventScroll: true });
  tiles[j]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
}

function Act({ a }: { a: RowAction }) {
  return (
    <Link
      {...(a.href ? { href: a.href } : { onPress: a.onPress })}
      {...(a.label ? { label: a.label } : {})}
      {...(a.isDisabled ? { isDisabled: true } : {})}
      size="hit-on-phone"
    >
      {a.text}
    </Link>
  );
}

/** Its lines, its actions at the end of the last one (on a line of their own when it has none). */
function Lines({ t }: { t: Tile }) {
  const lines: readonly ReactNode[] = t.lines ?? (t.photo.taken ? [t.photo.taken] : []);
  const acts = t.actions?.length ? t.actions.map((a) => <Act key={a.id} a={a} />) : null;
  const all = acts && !lines.length ? [null] : lines;
  return all.map((l, i) => (
    <span key={i} className="fl-tiles__line">
      {l}
      {i === all.length - 1 ? acts : null}
    </span>
  ));
}

export function PhotoTiles({
  tiles,
  label,
  layout = 'line',
  frame,
  lede,
  first,
  onOpen,
  emptyText = 'No photos yet',
}: PhotoTilesProps) {
  const [all, setAll] = useState(false);
  if (!tiles.length)
    return emptyText ? (
      <p className="fl-tiles__empty" data-layout={layout} {...(layout === 'grid' ? { role: 'status' } : {})}>
        {emptyText}
      </p>
    ) : null;
  const grid = layout === 'grid';
  const folded = first !== undefined && !all && tiles.length > first;
  const shown = folded ? tiles.slice(0, first) : tiles;
  const list = (
    <ul
      className="fl-tiles"
      data-layout={layout}
      aria-label={label}
      {...(grid ? {} : { onKeyDown: onLineKeyDown })}
    >
      {shown.map((t) => (
        <li key={t.photo.id} className="fl-tiles__item">
          <PhotoThumb
            photo={t.photo}
            size={grid ? 'fill' : 'tile'}
            {...(frame ? { frame } : {})}
            {...(t.openLabel ? { label: t.openLabel } : {})}
            {...(onOpen ? { onPress: onOpen } : {})}
          />
          {t.kicker || t.name ? (
            <b className="fl-tiles__name">
              {t.kicker ? <span className="fl-tiles__kicker">{t.kicker}</span> : null}
              {t.kicker && t.name ? ' ' : null}
              {t.name}
            </b>
          ) : null}
          <Lines t={t} />
        </li>
      ))}
    </ul>
  );
  if (!lede && !folded) return list;
  return (
    <>
      {lede ? <div className="fl-tiles__lede">{lede}</div> : null}
      {list}
      {folded ? (
        <div className="fl-tiles__more">
          <Link onPress={() => setAll(true)}>{`Show all ${tiles.length}`}</Link>
        </div>
      ) : null}
    </>
  );
}
